import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useBusinessContext } from '../contexts/BusinessContext';
import { useMobileNav } from '../contexts/MobileNavContext';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, User, Building2, Menu, X, Plus, Phone, Mail, Calendar } from 'lucide-react';
import axios from 'axios';
import { useDateFormatter } from '../utils/dateUtils';
import ImageModal from './ImageModal';

const Header = () => {
    const { user } = useAuth();
    const { currentBusiness, isAdvisor } = useBusinessContext();
    const { toggleMobileMenu } = useMobileNav();
    const navigate = useNavigate();
    const { formatDateTime, timezone } = useDateFormatter();

    // Alerts state
    const [showAlerts, setShowAlerts] = useState(false);
    const [alerts, setAlerts] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [showCreateAlert, setShowCreateAlert] = useState(false);
    const [newAlert, setNewAlert] = useState({ title: '', message: '', alert_type: 'info' });
    const alertsRef = useRef(null);

    // Contact advisor state
    const [showContactAdvisor, setShowContactAdvisor] = useState(false);
    const contactRef = useRef(null);

    // Image modal state
    const [imageModalOpen, setImageModalOpen] = useState(false);
    const [imageModalUrl, setImageModalUrl] = useState('');

    // For advisors, only show business info when viewing a specific business
    // For business owners, show their own business info from currentBusiness
    const shouldShowBusinessInfo = currentBusiness;
    const displayBusinessName = currentBusiness?.business_name || currentBusiness?.name;
    const displayIndustry = currentBusiness?.industry;
    const displayLogoUrl = currentBusiness?.logo_url;

    // Fetch alerts
    const fetchAlerts = async () => {
        try {
            const token = localStorage.getItem('token');
            const businessId = currentBusiness?.id || user?.business_id;

            // For advisors, always fetch alerts (backend returns all their business alerts)
            // For users, only fetch if we have a business context
            if (!isAdvisor && !businessId) {
                return;
            }

            const headers = {
                'Authorization': `Bearer ${token}`
            };

            // Include business ID if available (though backend ignores it for advisors)
            if (businessId) {
                headers['X-Business-Id'] = businessId;
            }

            const response = await axios.get(
                `${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/alerts/`,
                { headers }
            );
            setAlerts(response.data.alerts || []);
            setUnreadCount(response.data.unread_count || 0);
        } catch (error) {
            // Silently fail if no business context
            if (error.response?.status !== 400) {
                console.error('Error fetching alerts:', error);
            }
        }
    };

    // Fetch alerts on mount and when business context changes
    useEffect(() => {
        const businessId = currentBusiness?.id || user?.business_id;
        // Fetch for advisors always, or for users if they have a business context
        if (isAdvisor || businessId) {
            fetchAlerts();
        }
    }, [currentBusiness?.id, user?.business_id, isAdvisor]);

    // Close dropdowns when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (alertsRef.current && !alertsRef.current.contains(event.target)) {
                setShowAlerts(false);
                setShowCreateAlert(false);
            }
            if (contactRef.current && !contactRef.current.contains(event.target)) {
                setShowContactAdvisor(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Mark alert as read
    const markAsRead = async (alertId) => {
        try {
            const businessId = currentBusiness?.id || user?.business_id;
            const token = localStorage.getItem('token');
            await axios.put(
                `${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/alerts/${alertId}`,
                { is_read: true },
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'X-Business-Id': businessId
                    }
                }
            );
            fetchAlerts();
        } catch (error) {
            console.error('Error marking alert as read:', error);
        }
    };

    // Delete alert
    const deleteAlert = async (alertId) => {
        try {
            const businessId = currentBusiness?.id || user?.business_id;
            const token = localStorage.getItem('token');
            await axios.delete(
                `${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/alerts/${alertId}`,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'X-Business-Id': businessId
                    }
                }
            );
            fetchAlerts();
        } catch (error) {
            console.error('Error deleting alert:', error);
        }
    };

    // Create new alert
    const handleCreateAlert = async (e) => {
        e.preventDefault();
        try {
            const businessId = currentBusiness?.id || user?.business_id;
            const token = localStorage.getItem('token');
            await axios.post(
                `${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/alerts/`,
                newAlert,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'X-Business-Id': businessId
                    }
                }
            );
            setNewAlert({ title: '', message: '', alert_type: 'info' });
            setShowCreateAlert(false);
            fetchAlerts();
        } catch (error) {
            console.error('Error creating alert:', error);
        }
    };

    // Mark all as read
    const markAllAsRead = async () => {
        try {
            const businessId = currentBusiness?.id || user?.business_id;
            const token = localStorage.getItem('token');
            await axios.post(
                `${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/alerts/mark-all-read`,
                {},
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'X-Business-Id': businessId
                    }
                }
            );
            fetchAlerts();
        } catch (error) {
            console.error('Error marking all as read:', error);
        }
    };

    const getAlertColor = (type) => {
        switch (type) {
            case 'urgent': return 'text-red-600 bg-red-50';
            case 'warning': return 'text-yellow-600 bg-yellow-50';
            default: return 'text-blue-600 bg-blue-50';
        }
    };

    return (
        <>
        <header className="bg-white border-b border-gray-200 px-4 sm:px-6 py-3 sm:py-4">
            <div className="flex items-center justify-between">
                {/* Left side - Hamburger menu (mobile only) */}
                <button
                    onClick={toggleMobileMenu}
                    className="md:hidden p-2 hover:bg-gray-100 rounded-lg transition min-h-[44px] min-w-[44px] flex items-center justify-center"
                    aria-label="Open menu"
                >
                    <Menu size={24} className="text-gray-700" />
                </button>

                {/* Right side - Business info and User info */}
                <div className="flex items-center gap-2 sm:gap-4 ml-auto">
                    {/* Alerts Button */}
                    <div className="relative" ref={alertsRef}>
                        <button
                            onClick={() => setShowAlerts(!showAlerts)}
                            className="p-2 hover:bg-gray-100 rounded-lg transition relative min-h-[44px] min-w-[44px] flex items-center justify-center"
                        >
                            <AlertTriangle size={20} className="text-gray-600" />
                            {unreadCount > 0 && (
                                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                                    {unreadCount}
                                </span>
                            )}
                        </button>

                        {/* Alerts Dropdown */}
                        {showAlerts && (
                            <div className="absolute right-0 mt-2 w-[calc(100vw-2rem)] sm:w-96 max-w-md bg-white rounded-lg shadow-xl border border-gray-200 z-50 max-h-[80vh] sm:max-h-96 overflow-hidden flex flex-col">
                                {/* Header */}
                                <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                                    <h3 className="font-semibold text-gray-900">Alerts</h3>
                                    <div className="flex gap-2">
                                        {unreadCount > 0 && (
                                            <button
                                                onClick={markAllAsRead}
                                                className="text-xs text-blue-600 hover:text-blue-700"
                                            >
                                                Mark all read
                                            </button>
                                        )}
                                        <button
                                            onClick={() => setShowCreateAlert(!showCreateAlert)}
                                            className="p-1 hover:bg-gray-100 rounded"
                                        >
                                            <Plus size={16} />
                                        </button>
                                    </div>
                                </div>

                                {/* Create Alert Form */}
                                {showCreateAlert && (
                                    <div className="p-4 border-b border-gray-200 bg-gray-50">
                                        <form onSubmit={handleCreateAlert} className="space-y-3">
                                            <input
                                                type="text"
                                                placeholder="Alert Title"
                                                value={newAlert.title}
                                                onChange={(e) => setNewAlert({ ...newAlert, title: e.target.value })}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-base"
                                                required
                                            />
                                            <textarea
                                                placeholder="Alert Message"
                                                value={newAlert.message}
                                                onChange={(e) => setNewAlert({ ...newAlert, message: e.target.value })}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-base"
                                                rows="3"
                                                required
                                            />
                                            <div className="flex flex-col sm:flex-row gap-2">
                                                <select
                                                    value={newAlert.alert_type}
                                                    onChange={(e) => setNewAlert({ ...newAlert, alert_type: e.target.value })}
                                                    className="px-3 py-2 border border-gray-300 rounded-lg text-base"
                                                >
                                                    <option value="info">Info</option>
                                                    <option value="warning">Warning</option>
                                                    <option value="urgent">Urgent</option>
                                                </select>
                                                <button
                                                    type="submit"
                                                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 min-h-[44px]"
                                                >
                                                    Create Alert
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => setShowCreateAlert(false)}
                                                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg text-sm hover:bg-gray-300 min-h-[44px]"
                                                >
                                                    Cancel
                                                </button>
                                            </div>
                                        </form>
                                    </div>
                                )}

                                {/* Alerts List */}
                                <div className="overflow-y-auto flex-1">
                                    {alerts.length === 0 ? (
                                        <div className="p-4 text-center text-gray-500 text-sm">
                                            No alerts
                                        </div>
                                    ) : (
                                        alerts.map((alert) => (
                                            <div
                                                key={alert.id}
                                                className={`p-4 border-b border-gray-100 hover:bg-gray-50 ${!alert.is_read ? 'bg-blue-50' : ''}`}
                                            >
                                                <div className="flex items-start justify-between gap-2">
                                                    <div className="flex-1">
                                                        <div className="flex items-center gap-2 mb-1">
                                                            <span className={`text-xs px-2 py-1 rounded ${getAlertColor(alert.alert_type)}`}>
                                                                {alert.alert_type}
                                                            </span>
                                                            {!alert.is_read && (
                                                                <span className="w-2 h-2 bg-blue-600 rounded-full"></span>
                                                            )}
                                                        </div>
                                                        {/* Show business info for advisors */}
                                                        {isAdvisor && (alert.business_name || alert.business_owner_name) && (
                                                            <div className="mb-2 px-2 py-1 bg-gray-100 rounded text-xs">
                                                                <span className="font-semibold text-gray-700">
                                                                    {alert.business_name || 'Business'}
                                                                </span>
                                                                {alert.business_owner_name && (
                                                                    <span className="text-gray-600"> • {alert.business_owner_name}</span>
                                                                )}
                                                            </div>
                                                        )}
                                                        <h4 className="font-medium text-sm text-gray-900">{alert.title}</h4>
                                                        <p className="text-xs text-gray-600 mt-1">{alert.message}</p>
                                                        <p className="text-xs text-gray-400 mt-2">
                                                            {formatDateTime(alert.created_at)}
                                                            {alert.created_by_display && ` • ${alert.created_by_display}`}
                                                        </p>
                                                    </div>
                                                    <div className="flex gap-1">
                                                        {!alert.is_read && (
                                                            <button
                                                                onClick={() => markAsRead(alert.id)}
                                                                className="p-1 text-blue-600 hover:bg-blue-100 rounded text-xs"
                                                                title="Mark as read"
                                                            >
                                                                ✓
                                                            </button>
                                                        )}
                                                        <button
                                                            onClick={() => deleteAlert(alert.id)}
                                                            className="p-1 text-red-600 hover:bg-red-100 rounded"
                                                            title="Delete"
                                                        >
                                                            <X size={14} />
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        ))
                                    )}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Contact Advisor Button */}
                    <div className="relative" ref={contactRef}>
                        <button
                            onClick={() => setShowContactAdvisor(!showContactAdvisor)}
                            className="p-2 hover:bg-gray-100 rounded-lg transition relative min-h-[44px] min-w-[44px] flex items-center justify-center"
                            title="Contact Your Advisor"
                        >
                            <Phone size={20} className="text-gray-600" />
                        </button>

                        {/* Contact Advisor Dropdown */}
                        {showContactAdvisor && (
                            <div className="absolute right-0 mt-2 w-[calc(100vw-2rem)] sm:w-80 max-w-sm bg-white rounded-lg shadow-xl border border-gray-200 z-50">
                                {/* Header */}
                                <div className="p-4 border-b border-gray-200">
                                    <h3 className="font-semibold text-gray-900">Contact Your Advisor</h3>
                                </div>

                                {/* Contact Info */}
                                <div className="p-4 space-y-4">
                                    {/* Email */}
                                    <div className="flex items-center gap-3">
                                        <div className="p-2 bg-blue-50 rounded-lg">
                                            <Mail size={18} className="text-blue-600" />
                                        </div>
                                        <div className="flex-1">
                                            <div className="text-xs text-gray-500 mb-1">Email</div>
                                            <a
                                                href="mailto:info@dominionbusinessadvisors.com"
                                                className="text-sm text-blue-600 hover:text-blue-700 hover:underline"
                                            >
                                                info@dominionbusinessadvisors.com
                                            </a>
                                        </div>
                                    </div>

                                    {/* Phone */}
                                    <div className="flex items-center gap-3">
                                        <div className="p-2 bg-green-50 rounded-lg">
                                            <Phone size={18} className="text-green-600" />
                                        </div>
                                        <div className="flex-1">
                                            <div className="text-xs text-gray-500 mb-1">Phone</div>
                                            <a
                                                href="tel:+14808058996"
                                                className="text-sm text-green-600 hover:text-green-700 hover:underline"
                                            >
                                                (480) 805-8996
                                            </a>
                                        </div>
                                    </div>

                                    {/* Schedule Appointment */}
                                    <div className="pt-2 border-t border-gray-200">
                                        <a
                                            href="https://calendly.com/cameronteich/client-strategy-and-advisory-session"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="flex items-center justify-center gap-2 w-full px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition min-h-[44px]"
                                        >
                                            <Calendar size={18} />
                                            <span className="font-medium text-sm sm:text-base">Schedule Appointment</span>
                                        </a>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Business Name & Industry */}
                    {shouldShowBusinessInfo && (
                        <div className="hidden sm:flex items-center gap-2 px-3 py-2 bg-blue-50 rounded-lg border border-blue-200">
                            {displayLogoUrl ? (
                                <>
                                    <img
                                        src={displayLogoUrl}
                                        alt={`${displayBusinessName} logo`}
                                        className="w-8 h-8 object-contain rounded cursor-pointer hover:opacity-80 transition-opacity"
                                        onClick={() => {
                                            setImageModalUrl(displayLogoUrl);
                                            setImageModalOpen(true);
                                        }}
                                        onError={(e) => {
                                            console.error('[Header] Logo failed to load:', displayLogoUrl);
                                            e.target.style.display = 'none';
                                        }}
                                        onLoad={() => {
                                            // Logo loaded successfully
                                        }}
                                        title="Click to view full size"
                                    />
                                    <Building2 size={18} className="text-blue-600" style={{ display: 'none' }} />
                                </>
                            ) : (
                                <Building2 size={18} className="text-blue-600" />
                            )}
                            <span className="text-sm font-medium text-blue-900">
                                {displayBusinessName}
                                {displayIndustry && (
                                    <span className="text-blue-600 ml-1">| {displayIndustry}</span>
                                )}
                            </span>
                        </div>
                    )}

                    {/* User Email - Clickable */}
                    <button
                        onClick={() => navigate('/account')}
                        className="flex items-center gap-2 px-3 py-2 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors cursor-pointer min-h-[44px]"
                    >
                        <User size={18} className="text-gray-600" />
                        <span className="text-sm font-medium text-gray-700 hidden sm:inline">
                            {user?.email || 'User'}
                        </span>
                    </button>
                </div>
            </div>
        </header>

        {/* Image Modal for Logo Preview */}
        <ImageModal
            isOpen={imageModalOpen}
            imageUrl={imageModalUrl}
            altText="Business logo"
            onClose={() => setImageModalOpen(false)}
        />
        </>
    );
};

export default Header;